import {
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  MODULE_FLOW_DIRECTION_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks/useModuleStyles';
import { BaseModuleProps } from '@/common/types';
import { HeadingBlock, HeadingBlockProps } from '@/components/blocks/HeadingBlock';
import { ImageBlock } from '@/components/blocks/ImageBlock';
import { ImageBlockProps } from '@/components/blocks/ImageBlock/ImageBlock';
import { ParagraphContent, ParagraphContentProps } from '@/components/blocks/ParagraphContent';
import classNames from 'classnames';
import React from 'react';

import styles from './Resource.module.scss';

export interface ResourceProps extends BaseModuleProps {
  /** Image for the resource */
  image: ImageBlockProps;
  /** Paragraphs for the resource */
  paragraphs: ParagraphContentProps[];
}

/**
 * Resource module component
 * @param props - The props for the Resource module
 * @returns The Resource module component
 */
export const Resource: React.FC<ResourceProps> = ({
  anchorTag = undefined,
  container = CONTAINER_OPTIONS.CONTAINED,
  fillType = MODULE_FILL_TYPE_OPTIONS.BASE,
  gradientFillToken,
  layout = CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
  eyebrow,
  title,
  titleSize = TITLE_SIZE_OPTIONS.LARGE,
  titleType = TITLE_TYPE_OPTIONS.H2,
  bodyRichTextAsHTML,
  alignment = HEADING_ALIGNMENT_OPTIONS.CENTER,
  ctas,
  caption,
  paragraphs,
  image,
}) => {
  const headingConfig: HeadingBlockProps = {
    eyebrow,
    title,
    titleSize,
    titleType,
    bodyRichTextAsHTML,
    alignment,
    ctas,
    caption,
  };

  // Use the common module styles hook
  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container,
    fillType,
    gradientFillToken,
    flowDirection: MODULE_FLOW_DIRECTION_OPTIONS.COLUMN,
    layout,
    moduleClassName: 'module-resource',
    styles,
  });

  return (
    <section className={sectionStyles} id={anchorTag}>
      <div className={moduleBaseStyles}>
        <HeadingBlock {...headingConfig} />
        <div className={styles['module-content']}>
          <ImageBlock
            {...image}
            className={styles['module-image']}
            responsive={{
              compactWidth: 514,
              desktopWidth: 514,
              sizes: '(max-width: 600px) 100vw, 514px',
            }}
          />
          <div className={classNames(styles['module-paragraph-container'])}>
            {paragraphs.map((paragraphContentProps: ParagraphContentProps, idx: number) => (
              <ParagraphContent
                key={idx}
                {...paragraphContentProps}
                showDivider={idx !== paragraphs.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
